import React, { useCallback, useEffect, useState } from 'react';
import ReactTable from 'react-table';
import moment from 'moment-timezone';

import { ChecklistImage } from '../Checklist';
import { PastChecklistsService, TimeFormattingService } from '../../services';
import i18next from "i18next";
import { connect } from 'react-redux';

import config from "config";

const ChecklistPhoto = ({photos}) => ( 
  photos.map((photo, index) => <div key={index}><ChecklistImage imgUrl={photo.photoURL}/></div>) 
);

const ChecklistStep = connect(({ user }) => ({ me: user.me }))(({ step, timezone, me }) => {
  var stepResult = null;
  switch (step.type) {
    case 'selection':
      stepResult = step.pastChecklistTaskSelectedOptions.map(selection => selection.checklistTaskSelectionOption.name).join(', ');
      break;
    case 'temperature':
      stepResult = step.temperature ? 
          (me.temperatureDisplayUnit == "F" ? sprintf("%0.2f F", (step.temperature * 1.8) + 32) : sprintf("%0.2f C", step.temperature)) : 
          '';
      break;
    case 'photo':
      stepResult = step.pastChecklistTaskPhotos && step.pastChecklistTaskPhotos.length > 0 ? <ChecklistPhoto photos={step.pastChecklistTaskPhotos} /> : '';
      break;
    case 'note':
      stepResult = step.note ?? '';
      break;
  }

  return stepResult ? (
    <div>
      <b>{stepResult}</b> <br/> <br/>
      {step.startedAt ? TimeFormattingService.renderShortTime(step.startedAt, timezone) : 'Never'} <br/>
      to <br/>
      {step.completedAt ? TimeFormattingService.renderShortTime(step.completedAt, timezone) : (step.startedAt ? 'Never' : 'N/A')} <br/>
    </div>
  ) : <b>Skipped</b>;
});

const ChecklistHeader = ({timestamp, userByTime, timezone}) => {
  let date = TimeFormattingService.renderDate(timestamp, timezone);
  let time = TimeFormattingService.renderTime(timestamp, timezone);
  return userByTime[timestamp] ? (<span>{date}<br/>{time}<br/>{userByTime[timestamp]}</span>) : (<span>{date}<br/>{time}</span>);
};

const ChecklistEntry = ({entry, timezone}) => {
  let step = entry?.stepTaken ? <ChecklistStep step={entry.stepTaken} timezone={timezone} /> : <b>Skipped</b>;
  let correctiveActionsTaken = null;
  
  if (entry?.correctiveActions) {
    const caSteps = Object.values(entry.correctiveActions).map(ca => {
      if (ca.pastChecklistTaskOptions) {
        return ca.pastChecklistTaskOptions.map((caStep) => { return <div key={caStep.id}>{caStep.checklistTaskOptionConfig.name}: <ChecklistStep step={caStep} /></div>});
      } else if (ca.checklistTaskConfig && ca.checklistTaskConfig.checklistTaskOptionConfigs) {
        return ca.checklistTaskConfig.checklistTaskOptionConfigs.map((caStep) => { return <div key={caStep.id}>{caStep.name}: </div>});
      }
    });

    correctiveActionsTaken = caSteps.length > 0 ? (
      <div style={{paddingTop: 10}}>
        <b>Corrective Actions</b> <br/>
        {caSteps}
      </div>
    ) : null;
  };

  return (<span>{step} {correctiveActionsTaken}</span>)
};

const ChecklistSummaryReportResults = ({ query }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [timezone, setTimezone] = useState(null);
    const [exportLoading, setExportLoading] = useState(false);
    
    const constructOptions = useCallback(() => {
      const DATE_FORMAT = 'L';
      const timezone = query.location.timezone;
      const startAt = moment(query.startAtDate, DATE_FORMAT);
      const endAt = moment(query.startAtDate, DATE_FORMAT).add(6, 'day');

      const startAtDate = timezone ? startAt.tz(timezone).startOf('day').utc().valueOf() : startAt.startOf('day').utc().valueOf();
      const endAtDate =  timezone ? endAt.tz(timezone).endOf('day').utc().valueOf() : endAt.endOf('day').utc().valueOf();

      return [
        {'checklistId': query.checklist.id},
        {'groupingId': query.location.id},
        {'startDate': startAtDate},
        {'endDate': endAtDate}
      ];
  }, [query,]);

  useEffect(() => {
    if (query.location && query.startAtDate && query.checklist) {
      setTimezone(query.location.timezone);
      setLoading(true);

      PastChecklistsService.getSummaryReport(constructOptions()).then((response) => {
        setData(response.response.data);
        setLoading(false);
      }).catch((error) => {
        console.error(error);
        setLoading(false);
      });
    }
  }, [query]);

  const constructColumns = (data) => {
    let dynamicColumns = [ {
      Header: i18next.t('table-headers.task'),
      accessor: "taskName",
      minWidth: 200,
      minResizeWidth: 100,
      sortable: false
    }, {
      Header: i18next.t('table-headers.step'),
      accessor: "stepName",
      minWidth: 150,
      minResizeWidth: 100,
      sortable: false
    }];

    if (data && data[0] && data[0].entryByTime) {
      Object.keys(data[0].entryByTime)
        .sort((a, b) => {return parseInt(a) - parseInt(b)})
        .map((timestamp) => {
          dynamicColumns.push({
            id: timestamp,
            Header: timestamp ? <ChecklistHeader timestamp={parseInt(timestamp)} userByTime={data[0].userByTime} timezone={timezone} /> : '',
            accessor: `entryByTime[${timestamp}]`,
            Cell: cell => <ChecklistEntry timezone={timezone} entry={cell.value} />,
            sortable: false,
            minWidth: 100,
            minResizeWidth: 100
          });
        });
    }

    return dynamicColumns;
  }

  const exportData = () => {
    setExportLoading(true);

    PastChecklistsService.getSummaryReportCSV(constructOptions()).then(() => {
      setExportLoading(false);
    }).catch((error) => {
      console.error(error);
      setExportLoading(false);
    });    
  };

  if (loading) {
    return (<div className="loader">{ i18next.t('loading') }</div>);
  } else if (data.length === 0) {
    return (<div><p>{ i18next.t('messages.no-completed-checklists-found') }</p></div>);
  } else {
    return (
      <div>
        <div className="op-content-box">
          <ul className="list-inline clearfix">
            <li className="pull-left box-label"><h4 className="remove-margin-bottom">{ i18next.t('results') }</h4></li>
          </ul>

          <div>
            <a className="tooltip pull-right">
              <img id="export-icon" className={exportLoading ? "spinner" : ""} src={`${config.WEB_BASE_URL}/images/icon/themes/${window.globalBrand}/export_icon.svg`} onClick={exportData} alt={ i18next.t('table-headers.export-data') } /> 
              <span className="tooltiptext">{ i18next.t('table-headers.export-data') }</span>
            </a>
            <br className='clearfix' />
          </div> 

          <div>          
          {
            data.map((config, index) =>
              <div key={index} className="react-table-custom" style={{ paddingTop: 5, paddingBottom: 50 }}>
                { data.length > 1 ? <div style={{ fontSize: '150%', fontWeight: 'bold' }}>Version {config.versionNumber}</div> : '' }
                <ReactTable
                  className="-striped -highlight"
                  data={config.checklistEntries}
                  showPagination={false}
                  pageSize={config.checklistEntries.length}
                  loading={loading} // Display the loading overlay when we need it
                  columns={constructColumns(config.checklistEntries)}
                />
                <br/>
              </div>
            )
          }
          </div>
        </div>
      </div>
    );
  }  
};

export default ChecklistSummaryReportResults;
